'use client';

import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { UserCheck, Shield, CheckCircle2, XCircle, ChevronDown, ChevronUp, Lock, Eye, Edit3, Trash2, Layers, CreditCard } from 'lucide-react';
import { useToast } from './ui/Toast';

interface PendingRequest {
  id: string;
  memberId: string;
  targetAdminId: string;
  status: string;
  createdAt: string;
  member: {
    id: string;
    email: string;
    name: string;
  };
}

interface WorkspaceBoardCard {
  id: string;
  title: string;
}

interface WorkspaceBoard {
  id: string;
  title: string;
  cards?: WorkspaceBoardCard[];
}

interface WorkspaceItem {
  id: string;
  name: string;
  boards?: WorkspaceBoard[];
}

export default function PendingMemberRequests() {
  const { showToast } = useToast();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Selected state per request ID
  const [selectedWs, setSelectedWs] = useState<{ [reqId: string]: string }>({});
  const [selectedWsRole, setSelectedWsRole] = useState<{ [reqId: string]: 'GUEST' | 'MEMBER' }>({});
  const [boardRoles, setBoardRoles] = useState<{ [reqId: string]: { [boardId: string]: 'NONE' | 'BOARD_VIEWER' | 'BOARD_MEMBER' } }>({});
  const [cardAccess, setCardAccess] = useState<{ [reqId: string]: { [cardId: string]: 'FULL_ACCESS' | 'EDIT_ONLY' | 'VIEW_ONLY' } }>({});
  
  const [expandedReq, setExpandedReq] = useState<string | null>(null);
  const [workspaceDetails, setWorkspaceDetails] = useState<{ [wsId: string]: WorkspaceItem }>({});
  const [loadingWsDetails, setLoadingWsDetails] = useState<boolean>(false);

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [reqRes, meRes] = await Promise.all([
        api.get('/admin/member-requests'),
        api.get('/auth/me'),
      ]);
      setRequests(reqRes.data?.data || []);

      const userWorkspaces = (meRes.data?.data?.workspaceMembers || []).map((m: any) => m.workspace);
      setWorkspaces(userWorkspaces);
    } catch (err: any) {
      console.error('Failed to fetch pending member requests', err);
      setLoadError(err?.response?.data?.error?.message || 'Could not load pending member approval requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch boards and cards when workspace is selected for a request
  const loadWorkspaceDetails = async (wsId: string) => {
    if (!wsId || workspaceDetails[wsId]) return;
    setLoadingWsDetails(true);
    try {
      const res = await api.get(`/workspaces/${wsId}`);
      if (res.data?.data) {
        setWorkspaceDetails((prev) => ({ ...prev, [wsId]: res.data.data }));
      }
    } catch (err) {
      console.error('Failed to load workspace details for permissions', err);
    } finally {
      setLoadingWsDetails(false);
    }
  };

  const handleWorkspaceChange = (reqId: string, wsId: string) => {
    setSelectedWs((prev) => ({ ...prev, [reqId]: wsId }));
    loadWorkspaceDetails(wsId);
  };

  const handleToggleExpand = (reqId: string) => {
    const isExpanding = expandedReq !== reqId;
    setExpandedReq(isExpanding ? reqId : null);

    if (isExpanding) {
      const targetWsId = selectedWs[reqId] || workspaces[0]?.id;
      if (targetWsId) {
        setSelectedWs((prev) => ({ ...prev, [reqId]: targetWsId }));
        loadWorkspaceDetails(targetWsId);
      }
    }
  };

  const handleApprove = async (reqId: string) => {
    const wsId = selectedWs[reqId] || workspaces[0]?.id;
    if (!wsId) {
      showToast('Please select a workspace to grant access to.', 'error');
      return;
    }

    const currentBoardRoles = boardRoles[reqId] || {};
    const boardSelections = Object.entries(currentBoardRoles)
      .filter(([_, role]) => role !== 'NONE')
      .map(([boardId, role]) => ({ boardId, role: role as 'BOARD_VIEWER' | 'BOARD_MEMBER' }));

    const currentCardAccess = cardAccess[reqId] || {};
    const cardPermissions = Object.entries(currentCardAccess).map(([cardId, accessLevel]) => ({
      cardId,
      accessLevel,
    }));

    try {
      await api.post(`/admin/member-requests/${reqId}/approve`, {
        workspaceId: wsId,
        workspaceRole: selectedWsRole[reqId] || 'GUEST',
        boardSelections,
        cardPermissions,
      });
      showToast('Member permissions saved and request approved!', 'success');
      setRequests((prev) => prev.filter((r) => r.id !== reqId));
    } catch (err: any) {
      showToast(err?.response?.data?.error?.message || err?.response?.data?.message || 'Failed to approve member.', 'error');
    }
  };

  const handleReject = async (reqId: string) => {
    try {
      await api.post(`/admin/member-requests/${reqId}/reject`, {});
      showToast('Member access request rejected.', 'error');
      setRequests((prev) => prev.filter((r) => r.id !== reqId));
    } catch (err: any) {
      showToast(err?.response?.data?.error?.message || err?.response?.data?.message || 'Failed to reject request.', 'error');
    }
  };

  if (loading) return null;

  if (loadError) {
    return (
      <div className="mb-8 bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4 text-xs text-rose-300">
        {loadError}
      </div>
    );
  }

  if (requests.length === 0) return null;

  return (
    <div className="mb-8 bg-slate-900/90 border border-slate-800 backdrop-blur-xl rounded-2xl p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <UserCheck className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Pending Member Approval & Permission Requests</h3>
            <p className="text-xs text-slate-400">Review pending join requests and configure granular board/card permissions</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold rounded-full">
          {requests.length} Pending
        </span>
      </div>

      <div className="space-y-4">
        {requests.map((req) => {
          const currentWsId = selectedWs[req.id] || workspaces[0]?.id;
          const activeWsDetails = currentWsId ? workspaceDetails[currentWsId] : null;
          const activeBoards = activeWsDetails?.boards || [];

          return (
            <div key={req.id} className="bg-slate-950/80 border border-slate-800/90 rounded-xl p-4 transition-all">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-bold text-slate-100">{req.member?.name}</div>
                  <div className="text-xs text-slate-400">{req.member?.email}</div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleExpand(req.id)}
                    className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors"
                  >
                    <Shield className="w-3.5 h-3.5 text-indigo-400" />
                    <span>Set Permissions</span>
                    {expandedReq === req.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>

                  <button
                    onClick={() => handleReject(req.id)}
                    className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 text-xs font-bold rounded-xl flex items-center gap-1 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>

                  <button
                    onClick={() => handleApprove(req.id)}
                    className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-indigo-600/20 transition-all"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve & Save Permissions
                  </button>
                </div>
              </div>

              {expandedReq === req.id && (
                <div className="mt-4 pt-4 border-t border-slate-800/80 space-y-5 animate-in fade-in duration-200">
                  {/* Workspace & Main Role Selector */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Target Workspace
                      </label>
                      <select
                        value={currentWsId || ''}
                        onChange={(e) => handleWorkspaceChange(req.id, e.target.value)}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                      >
                        {workspaces.map((ws) => (
                          <option key={ws.id} value={ws.id}>{ws.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Workspace Access Role
                      </label>
                      <select
                        value={selectedWsRole[req.id] || 'GUEST'}
                        onChange={(e) => setSelectedWsRole({ ...selectedWsRole, [req.id]: e.target.value as any })}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                      >
                        <option value="GUEST">🔒 Guest (Restricted View-Only Access)</option>
                        <option value="MEMBER">👤 Member (Standard Team Member)</option>
                      </select>
                    </div>
                  </div>

                  {/* Board-Level Access Controls */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers className="w-4 h-4 text-indigo-400" />
                      <h4 className="text-xs font-bold text-white uppercase tracking-wider">Board Selection & Permissions</h4>
                    </div>

                    {loadingWsDetails ? (
                      <div className="text-xs text-slate-400 py-2">Loading workspace boards...</div>
                    ) : activeBoards.length === 0 ? (
                      <div className="text-xs text-slate-500 italic py-2">No boards found in this workspace.</div>
                    ) : (
                      <div className="space-y-3">
                        {activeBoards.map((board) => {
                          const currentBoardRole = boardRoles[req.id]?.[board.id] || 'NONE';
                          const isBoardSelected = currentBoardRole !== 'NONE';
                          const boardCards = board.cards || [];

                          return (
                            <div key={board.id} className="bg-slate-950 border border-slate-800/60 rounded-lg p-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Layers className="w-3.5 h-3.5 text-slate-500" />
                                  <span className="text-xs font-semibold text-slate-200">{board.title}</span>
                                </div>

                                <div className="flex items-center gap-2">
                                  <label className="text-[11px] text-slate-400">Access:</label>
                                  <select
                                    value={currentBoardRole}
                                    onChange={(e) => {
                                      const val = e.target.value as any;
                                      setBoardRoles((prev) => ({
                                        ...prev,
                                        [req.id]: { ...(prev[req.id] || {}), [board.id]: val },
                                      }));
                                    }}
                                    className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                                  >
                                    <option value="NONE">🚫 No Access (Hidden)</option>
                                    <option value="BOARD_VIEWER">👁️ Viewer (Read-only - Cannot Edit/Delete)</option>
                                    <option value="BOARD_MEMBER">✏️ Member (Edit & Collaborate)</option>
                                  </select>
                                </div>
                              </div>

                              {/* Card Action Granular Permissions */}
                              {isBoardSelected && boardCards.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-800/60 space-y-2 pl-4 border-l-2 border-indigo-500/30">
                                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                    <CreditCard className="w-3 h-3 text-indigo-400" />
                                    <span>Card Action Rights</span>
                                  </div>

                                  {boardCards.map((card) => {
                                    const currentCardPerm = cardAccess[req.id]?.[card.id] || 'VIEW_ONLY';
                                    return (
                                      <div key={card.id} className="flex items-center justify-between text-xs py-1">
                                        <span className="text-slate-300 truncate max-w-[200px] sm:max-w-[300px]">{card.title}</span>
                                        <select
                                          value={currentCardPerm}
                                          onChange={(e) => {
                                            const val = e.target.value as any;
                                            setCardAccess((prev) => ({
                                              ...prev,
                                              [req.id]: { ...(prev[req.id] || {}), [card.id]: val },
                                            }));
                                          }}
                                          className="bg-slate-900 border border-slate-800 rounded-lg px-2 py-0.5 text-[11px] text-slate-200"
                                        >
                                          <option value="VIEW_ONLY">👁️ View Only (Cannot Edit or Delete)</option>
                                          <option value="EDIT_ONLY">✏️ Edit Only (Can Edit, Cannot Delete)</option>
                                          <option value="FULL_ACCESS">⚡ Full Access (Edit & Delete)</option>
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
